import React, { useContext, useMemo, useState } from "react";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import sideImg from "../../assets/auth-side.png";
import { AuthContext } from "../../auth/AuthContext";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function AuthLogin() {
  const navigate = useNavigate();
  const q = useQuery();
  const presetEmail = q.get("email") || "";

  const { login } = useContext(AuthContext);

  const [email, setEmail] = useState(presetEmail);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onContinue = async () => {
    setError("");
    setSubmitting(true);

    try {
      await login({ email, password });
      navigate("/");
    } catch (err) {
      setError(err?.response?.data?.message || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout sideImage={sideImg}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate("/auth/email")} sx={{ ml: -1 }} aria-label="Back">
          <ArrowBackIcon />
        </IconButton>

        <Typography variant="h6">Finish logging in</Typography>
      </Box>

      <Typography sx={{ color: "text.secondary", mb: 3, maxWidth: 420 }}>
        Once you have entered the password you’ll be automatically logged in.
      </Typography>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Email (personal or work)
      </Typography>
      <TextField
        fullWidth
        placeholder="e.g. user@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
      />

      <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1 }}>
        Password
      </Typography>
      <TextField
        fullWidth
        placeholder="Enter password here"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
      />

      {error ? (
        <Typography sx={{ color: "error.main", mt: 1.5, fontSize: 13 }}>
          {error}
        </Typography>
      ) : null}

      <Button
        fullWidth
        variant="contained"
        disabled={submitting || !email || !password}
        onClick={onContinue}
        sx={{ mt: 4, opacity: submitting ? 0.8 : 1 }}
      >
        {submitting ? "Logging in..." : "Continue"}
      </Button>
    </AuthLayout>
  );
}
